import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { format, subDays } from 'date-fns';
import { useMultipleRealtimeSubscriptions } from '../../hooks/useRealtimeSubscription';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, FileCheck,
  Users, Calendar, Download, Filter, RefreshCw, MapPin
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import AdminSidebar from '../../components/ui/AdminSidebar';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({ start: format(subDays(new Date(), 30), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') });
  const [selectedMetric, setSelectedMetric] = useState('all');
  
  // Analytics data state
  const [metricsData, setMetricsData] = useState({
    totalApplications: 0,
    approvedApplications: 0,
    conversionRate: 0,
    totalRevenue: 0,
    activeVouchers: 0,
    verificationCount: 0
  });
  
  const [trendData, setTrendData] = useState([]);
  const [dealershipPerformance, setDealershipPerformance] = useState([]);
  const [voucherMetrics, setVoucherMetrics] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState([]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  // Real-time subscriptions for analytics data
  useMultipleRealtimeSubscriptions([
    {
      table: 'applications',
      onInsert: (newApp) => {
        console.log('[Analytics] New application:', newApp);
        // Refresh analytics when new application is created
        fetchAnalyticsData();
      },
      onUpdate: (updatedApp) => {
        console.log('[Analytics] Application updated:', updatedApp);
        // Refresh to update metrics and charts
        fetchAnalyticsData();
      },
      onDelete: (deletedApp) => {
        console.log('[Analytics] Application deleted:', deletedApp);
        fetchAnalyticsData();
      }
    },
    {
      table: 'vouchers',
      onInsert: (newVoucher) => {
        console.log('[Analytics] New voucher:', newVoucher);
        // Update voucher metrics in real-time
        fetchAnalyticsData();
      },
      onUpdate: (updatedVoucher) => {
        console.log('[Analytics] Voucher updated:', updatedVoucher);
        // Refresh voucher status distribution
        fetchAnalyticsData();
      },
      onDelete: (deletedVoucher) => {
        console.log('[Analytics] Voucher deleted:', deletedVoucher);
        fetchAnalyticsData();
      }
    },
    {
      table: 'voucher_verifications',
      onInsert: (newVerification) => {
        console.log('[Analytics] New verification:', newVerification);
        // Update verification count immediately
        setMetricsData(prev => ({
          ...prev,
          verificationCount: prev?.verificationCount + 1
        }));
        // Refresh full analytics data
        fetchAnalyticsData();
      },
      onUpdate: (updatedVerification) => {
        console.log('[Analytics] Verification updated:', updatedVerification);
        fetchAnalyticsData();
      },
      onDelete: (deletedVerification) => {
        console.log('[Analytics] Verification deleted:', deletedVerification);
        fetchAnalyticsData();
      }
    }
  ]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch applications with date filter
      const { data: applications, error: appsError } = await supabase?.from('applications')?.select('*, dealerships(name, city)')?.gte('application_date', dateRange?.start)?.lte('application_date', dateRange?.end);

      if (appsError) throw appsError;

      // Fetch vouchers
      const { data: vouchers, error: vouchersError } = await supabase?.from('vouchers')?.select('*, applications(customer_name), dealerships(name)')?.gte('issue_date', dateRange?.start)?.lte('issue_date', dateRange?.end);

      if (vouchersError) throw vouchersError;

      // Fetch verification history
      const { data: verifications, error: verificationsError } = await supabase?.from('voucher_verifications')?.select('*, vouchers(voucher_code), dealerships(name)')?.gte('verification_date', dateRange?.start)?.lte('verification_date', dateRange?.end);

      if (verificationsError) throw verificationsError;

      // Calculate metrics
      const totalApps = applications?.length || 0;
      const approvedApps = applications?.filter(app => app?.application_status === 'approved')?.length || 0;
      const conversionRate = totalApps > 0 ? ((approvedApps / totalApps) * 100)?.toFixed(1) : 0;
      const totalRevenue = applications?.reduce((sum, app) => sum + (parseFloat(app?.premium_amount) || 0), 0) || 0;
      const activeVouchers = vouchers?.filter(v => v?.voucher_status === 'active')?.length || 0;
      const verificationCount = verifications?.length || 0;

      setMetricsData({
        totalApplications: totalApps,
        approvedApplications: approvedApps,
        conversionRate: parseFloat(conversionRate),
        totalRevenue: totalRevenue,
        activeVouchers: activeVouchers,
        verificationCount: verificationCount
      });

      // Process trend data (last 7 days)
      const trendDays = [];
      for (let i = 6; i >= 0; i--) {
        const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
        const dayApps = applications?.filter(app => format(new Date(app?.application_date), 'yyyy-MM-dd') === date) || [];
        const dayVerifications = verifications?.filter(v => format(new Date(v?.verification_date), 'yyyy-MM-dd') === date) || [];
        
        trendDays?.push({
          date: format(subDays(new Date(), i), 'MMM dd'),
          applications: dayApps?.length,
          approvals: dayApps?.filter(app => app?.application_status === 'approved')?.length,
          verifications: dayVerifications?.length
        });
      }
      setTrendData(trendDays);

      // Calculate dealership performance
      const dealershipStats = {};
      applications?.forEach(app => {
        const dealershipName = app?.dealerships?.name || 'Unknown';
        if (!dealershipStats?.[dealershipName]) {
          dealershipStats[dealershipName] = {
            name: dealershipName,
            city: app?.dealerships?.city || '',
            total: 0,
            approved: 0,
            revenue: 0
          };
        }
        dealershipStats[dealershipName].total += 1;
        if (app?.application_status === 'approved') {
          dealershipStats[dealershipName].approved += 1;
        }
        dealershipStats[dealershipName].revenue += parseFloat(app?.premium_amount) || 0;
      });

      const performanceArray = Object.values(dealershipStats)?.map(d => ({
        ...d,
        conversionRate: d?.total > 0 ? ((d?.approved / d?.total) * 100)?.toFixed(1) : 0
      }))?.sort((a, b) => b?.revenue - a?.revenue);

      setDealershipPerformance(performanceArray);

      // Voucher metrics
      const voucherStats = {
        active: vouchers?.filter(v => v?.voucher_status === 'active')?.length || 0,
        used: vouchers?.filter(v => v?.voucher_status === 'used')?.length || 0,
        expired: vouchers?.filter(v => v?.voucher_status === 'expired')?.length || 0
      };

      setVoucherMetrics([
        { name: 'Active', value: voucherStats?.active, color: '#10b981' },
        { name: 'Used', value: voucherStats?.used, color: '#3b82f6' },
        { name: 'Expired', value: voucherStats?.expired, color: '#ef4444' }
      ]);

      // Status distribution
      const statusStats = {
        pending: applications?.filter(a => a?.application_status === 'pending')?.length || 0,
        approved: applications?.filter(a => a?.application_status === 'approved')?.length || 0,
        processing: applications?.filter(a => a?.application_status === 'processing')?.length || 0,
        rejected: applications?.filter(a => a?.application_status === 'rejected')?.length || 0
      };

      setStatusDistribution([
        { name: 'Pending', value: statusStats?.pending, color: '#f59e0b' },
        { name: 'Approved', value: statusStats?.approved, color: '#10b981' },
        { name: 'Processing', value: statusStats?.processing, color: '#3b82f6' },
        { name: 'Rejected', value: statusStats?.rejected, color: '#ef4444' }
      ]);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalyticsData();
  };

  const exportToCSV = () => {
    const headers = ['Dealership', 'City', 'Total Applications', 'Approved', 'Conversion Rate (%)', 'Revenue (€)'];
    const csvContent = [
      headers?.join(','),
      ...dealershipPerformance?.map(d => 
        `"${d?.name}","${d?.city}",${d?.total},${d?.approved},${d?.conversionRate},${d?.revenue?.toFixed(2)}`
      )
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a?.click();
  };

  const exportToPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="mt-1 text-sm text-gray-600">
                Comprehensive performance insights and metrics
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
              
              <button
                onClick={exportToPDF}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export PDF
              </button>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-500" />
              <input
                type="date"
                value={dateRange?.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e?.target?.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange?.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e?.target?.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e?.target?.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="all">All Metrics</option>
              <option value="applications">Applications Only</option>
              <option value="vouchers">Vouchers Only</option>
              <option value="revenue">Revenue Only</option>
            </select>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{metricsData?.conversionRate}%</p>
                  <p className="mt-1 text-sm text-emerald-600 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    +5.2% from last month
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <BarChart3 className="h-8 w-8 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">€{metricsData?.totalRevenue?.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="mt-1 text-sm text-emerald-600 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    +12.8% from last month
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Applications</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{metricsData?.totalApplications}</p>
                  <p className="mt-1 text-sm text-gray-600">
                    {metricsData?.approvedApplications} approved
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FileCheck className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Vouchers</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{metricsData?.activeVouchers}</p>
                  <p className="mt-1 text-sm text-gray-600">
                    Currently in circulation
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <FileCheck className="h-8 w-8 text-amber-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Verifications</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{metricsData?.verificationCount}</p>
                  <p className="mt-1 text-sm text-emerald-600 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    +8.3% this week
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Users className="h-8 w-8 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Avg. Processing Time</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">3.2 days</p>
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <TrendingDown className="h-4 w-4" />
                    -15% faster
                  </p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <Calendar className="h-8 w-8 text-indigo-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Trend Chart */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Legend />
                <Line type="monotone" dataKey="applications" stroke="#3b82f6" strokeWidth={2} name="Applications" />
                <Line type="monotone" dataKey="approvals" stroke="#10b981" strokeWidth={2} name="Approvals" />
                <Line type="monotone" dataKey="verifications" stroke="#8b5cf6" strokeWidth={2} name="Verifications" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dealership Performance */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Dealership Performance</h2>
                <MapPin className="h-5 w-5 text-gray-400" />
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {dealershipPerformance?.map((dealership, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{dealership?.name}</span>
                        <span className="text-xs text-gray-500">• {dealership?.city}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        {dealership?.approved} / {dealership?.total} approved ({dealership?.conversionRate}%)
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        €{dealership?.revenue?.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                      </div>
                      <div className="w-32 mt-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-emerald-600 h-2 rounded-full"
                          style={{ width: `${dealership?.conversionRate}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Voucher Status Distribution */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Voucher Status Distribution</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={voucherMetrics}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {voucherMetrics?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Application Status Bar Chart */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Application Status Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={statusDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                  {statusDistribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Real-time Alerts */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Alerts</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <TrendingUp className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-900">Milestone Achieved!</p>
                  <p className="text-sm text-emerald-700">Total revenue exceeded €10,000 this month</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <BarChart3 className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">High Performance</p>
                  <p className="text-sm text-blue-700">Premium Auto Group achieved 95% conversion rate this week</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Calendar className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Expiring Vouchers</p>
                  <p className="text-sm text-amber-700">15 vouchers will expire in the next 7 days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;